import React, { useEffect, useState } from 'react';
import { Alert, Form, Modal, Select, Tag } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { can, canZone, effectivePermissionDetails, permissionDetails } from './policy';
import { modules, actionLabels } from './catalog';
import { listPermissionGroups } from '../models/permissionGroupAPI';
import PermissionEditor from './PermissionEditor';

export default function UserPermissionEditor({ disabled = false }) {
  const form = Form.useFormInstance();
  const selectedIds = Form.useWatch('PermissionGroupIds', form) || form.getFieldValue('PermissionGroupIds') || [];
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      setLoading(true);
      listPermissionGroups().then(({ data }) => {
        if (!active) return;
        setGroups(data);
        setError(null);
        const currentIds = form.getFieldValue('PermissionGroupIds');
        if (currentIds) form.setFieldValue('PermissionGroupIds', currentIds.filter(id => data.some(group => group.id === id)));
      }).catch(() => { if (active) setError('Impossible de charger les groupes. Les affectations existantes sont conservées.'); })
        .finally(() => { if (active) setLoading(false); });
    };
    refresh();
    window.addEventListener('permission-groups-changed', refresh);
    return () => { active = false; window.removeEventListener('permission-groups-changed', refresh); };
  }, [form]);
  const available = loading || error ? [...groups, ...(form.getFieldValue('PermissionGroups') || []).filter(group => !groups.some(item => item.id === group.id))] : groups;
  const inheritedGroups = available.filter(group => selectedIds.includes(group.id));
  const unlockGroups = (sources, label) => {
    const ids = new Set(sources.map(group => group.id));
    const values = form.getFieldsValue(true);
    const before = { ...values, PermissionGroups: inheritedGroups };
    const after = { ...values, PermissionGroups: inheritedGroups.filter(group => !ids.has(group.id)) };
    const lost = modules.map(module => ({ label: module.label,
      actions: module.actions.filter(action => can(before, module.id, action) && !can(after, module.id, action)),
    })).filter(module => module.actions.length);
    const beforeDetails = effectivePermissionDetails(before);
    const afterDetails = effectivePermissionDetails(after);
    const losesDevisManager = !values.IsDevisManager && before.PermissionGroups.some(group => group.IsDevisManager) && !after.PermissionGroups.some(group => group.IsDevisManager);
    Modal.confirm({
      title: `Déverrouiller : ${label}`, width: 620,
      content: <div>
        <p>Ce droit est fixé par {sources.length > 1 ? 'ces groupes' : 'ce groupe'}. Pour le modifier, retirez {sources.length > 1 ? 'ces groupes' : 'ce groupe'} de cet utilisateur.</p>
        <div>{sources.map(group => <Tag color="blue" key={group.id}>{group.Name}</Tag>)}</div>
        <p><strong>Accès perdus avec ce retrait :</strong></p>
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {lost.map(module => <p key={module.label}><strong>{module.label}</strong> : {module.actions.map(action => actionLabels[action]).join(', ')}</p>)}
          {canZone(before) && !canZone(after) && <p>Zoning</p>}
          {losesDevisManager && <p>Devis Manager</p>}
          {!lost.length && <p>Aucun droit de module : les autres sources les conservent.</p>}
          {beforeDetails.consultation && !afterDetails.consultation && <Alert type="warning" message="Les prix d’achat deviendront visibles." />}
        </div>
        <p>Les droits directs et les autres groupes restent inchangés. Enregistrez ensuite la fiche utilisateur pour appliquer le retrait.</p>
      </div>,
      okText: sources.length > 1 ? 'Retirer ces groupes' : 'Retirer ce groupe', cancelText: 'Annuler', okButtonProps: { danger: true },
      onOk: () => form.setFieldValue('PermissionGroupIds', (form.getFieldValue('PermissionGroupIds') || []).filter(id => !ids.has(id))),
    });
  };
  return <div>
    <div className="permission-group-selection">
    {error && <Alert type="error" message={error} />}
    <Form.Item name="PermissionGroupIds" label={<strong><TeamOutlined /> Groupes de permissions</strong>}>
      <Select mode="multiple" allowClear optionFilterProp="label" placeholder="Ajouter un groupe…"
        loading={loading} disabled={disabled || loading || Boolean(error)}
        options={available.map(group => ({ value: group.id, label: group.Name }))} notFoundContent={loading ? 'Chargement…' : 'Aucun groupe disponible'} />
    </Form.Item>
    <div className="permission-group-chips">{inheritedGroups.map(group => {
      const settings = permissionDetails(group, true);
      const count = Object.values(settings.modules).filter(grant => grant.full || Object.values(grant.actions).some(Boolean)).length;
      return <div className="permission-group-chip" key={group.id}><strong>{group.Name}</strong><span className="permission-caption">{count} modules · droits ajoutés</span></div>;
    })}</div>
    <div className="permission-caption" style={{ marginTop: 10 }}>{inheritedGroups.length ? `${inheritedGroups.length} groupe(s) cumulés + droits directs ci-dessous` : 'Aucun groupe · droits directs uniquement'}</div>
    </div>
    <PermissionEditor disabled={disabled} inheritedGroups={inheritedGroups} onUnlockGroups={!loading && !error ? unlockGroups : undefined} />
  </div>;
}
